import { action, makeAutoObservable, observable, runInAction } from 'mobx';

import { GQL } from '../../../../../gql/client';
import {
  CreateCardInput,
  CreateUserInput,
  Finish3dSecureCardInfo,
  RegisterUserMutation,
  RegisterUserMutationVariables,
} from '../../../../../gql/gql-types';
import { registerUser } from '../../../../../gql/operations/registerUser';
import { Routes } from '../../../../constants/Routes';
import { Card } from '../../../../domains/customer/Card';
import { Lang } from '../../../../hooks/useLocale';
import { isValidName } from '../../../../utils/NameValidator';
import { CreditCardStore } from '../../store/CreditCardStore';

export type Name = {
  sei: string;
  mei: string;
};

/**
 * ユーザーの登録処理を司る
 */
export class ProfileStore {
  constructor(private lang: Lang = 'ja') {
    makeAutoObservable(this, {
      name: observable,
      setName: action,
      register: action,
      valid: observable,
      errors: observable,
      mail: observable,
      setMail: action,
      card: observable,
      setCard: action,
      phone: observable,
      setPhone: action,
    });
  }

  public valid = false;
  public name?: Name = undefined;
  public mail?: string = undefined;
  public phone?: string = undefined;
  public card?: Card = undefined;

  public errors: ErrorInfo[] = [];

  public setName(v: Name) {
    this.name = v;
    this.checkNameValidity();
  }

  public setMail(mail: string) {
    this.mail = mail;
  }

  public setCard(card?: Card) {
    this.card = card;
  }

  public setPhone(phone: string) {
    this.phone = phone;
    this.checkPhoneValidity();
  }

  public setLang(lang: Lang) {
    this.lang = lang;
  }

  private checkNameValidity() {
    runInAction(() => {
      const lang = this.lang;
      // 名前関連のエラーをクリア
      this.errors = this.errors.filter(error => error.field !== 'sei' && error.field !== 'mei');

      if (!this.name) {
        this.errors = [...this.errors, { field: 'sei', error: '氏名を入力してください。' }];
        this.valid = false;
        return;
      }
      if (!isValidName(this.name.sei, lang)) {
        this.errors = [
          ...this.errors,
          {
            field: 'sei',
            error: lang === 'en' ? 'Please use alphabet characters only.' : '氏名は全角カタカナで入力してください。',
          },
        ];
      }
      if (!isValidName(this.name.mei, lang)) {
        this.errors = [
          ...this.errors,
          {
            field: 'mei',
            error: lang === 'en' ? 'Please use alphabet characters only.' : '氏名は全角カタカナで入力してください。',
          },
        ];
      }

      // エラーが残っていればvalidをfalseに設定
      this.valid = this.errors.length === 0;
    });
  }

  private checkPhoneValidity() {
    runInAction(() => {
      // 電話番号関連のエラーをクリア
      this.errors = this.errors.filter(error => error.field !== 'phone');

      if (!this.phone) {
        this.errors = [...this.errors, { field: 'phone', error: '電話番号を入力してください。' }];
        this.valid = false;
        return;
      }

      if (this.phone.startsWith('+81')) {
        const localNumber = this.phone.substring(3);
        if (!/^\d{9,10}$/.test(localNumber)) {
          this.errors = [...this.errors, { field: 'phone', error: '10桁または11桁で入力してください。' }];
        }
      } else {
        if (!/^\+\d{2,}\d{7,11}$/.test(this.phone)) {
          this.errors = [...this.errors, { field: 'phone', error: '電話番号の桁数を確認してください。' }];
        }
      }
      // エラーが残っていればvalidをfalseに設定
      this.valid = this.errors.length === 0;
    });
  }

  public cardStatus: CreditCardStore = new CreditCardStore();

  /**
   * 事前に指定されたユーザー登録処理を行う。
   */
  public async register() {
    const user: CreateUserInput = {
      id: '_',
      // eslint-disable-next-line no-irregular-whitespace
      name: `${this.name?.sei}　${this.name?.mei}` || '',
      mail: this.mail,
      confirmed: [],
      sei: this.name?.sei,
      mei: this.name?.mei,
      phone: this.phone,
      insuranceCards: [],
    };

    const card: CreateCardInput | undefined = this.card
      ? {
          id: '_',
          cardCustomerId: '_',
          fourDigit: this.card.fourDigit || '',
          brand: this.card.brand || '',
          payjpID: this.card.id || '',
          expAt: this.card.expAt || '',
        }
      : undefined;

    return GQL.query<RegisterUserMutationVariables, RegisterUserMutation>(registerUser, {
      input: {
        user,
        card,
      },
    });
  }

  /**
   * 3Dセキュア前に、今の入力状態をブラウザのsessionStorageへ退避
   * 3Dセキュアの画面へ遷移してstoreが壊れても復元可能にする
   */
  public saveToSession() {
    const data = {
      name: this.name,
      phone: this.phone,
      mail: this.mail,
      lang: this.lang,
    };
    sessionStorage.setItem('tempProfile', JSON.stringify(data));
  }

  /**
   * 3Dセキュア後、別画面でstoreを作り直すために復元する
   */
  public loadFromSession() {
    const json = sessionStorage.getItem('tempProfile');
    if (!json) return;

    try {
      const data = JSON.parse(json);
      data.name && this.setName(data.name);
      data.phone && this.setPhone(data.phone);
      data.mail && this.setMail(data.mail);
      data.lang && this.setLang(data.lang);
    } catch (e) {
      console.error('Failed to load profile from session:', e);
    }
  }

  /**
   * register()後にsessionStorageをクリアする
   */
  public clearSession() {
    sessionStorage.removeItem('tempProfile');
  }

  /**
   * 3Dセキュア完了後の処理
   * 他のstoreにも同様のメソッドを実装しているが、各storeでメソッド名やフローが異なるため処理内容の統一はしていない
   */
  public async completeAfter3DSecure(params: { cardInfo?: Finish3dSecureCardInfo | null }): Promise<{
    redirectPath: string;
    fixedId?: string;
    toastMessage: string;
  }> {
    try {
      if (params.cardInfo) {
        this.setCard({
          id: params.cardInfo.payjpID,
          fourDigit: params.cardInfo.fourDigit,
          brand: params.cardInfo.brand,
          expAt: params.cardInfo.expAt,
        });
      }
      this.loadFromSession();
      await this.register();
      // 正常終了時の返却値を追加
      return {
        // 外国人の場合、訪日対応は診療科が限定されているため、患者登録後に空き枠検索画面へ直接遷移させる
        redirectPath:
          this.lang === 'en' ? `${Routes.Paths.ReservationNew}?department=99#VacancySelect` : Routes.Paths.Account,
        toastMessage: this.lang === 'en' ? 'Registration completed.' : '登録が完了しました。',
      };
    } finally {
      // 成功でも失敗でもセッション上の一時データは掃除する
      this.clearSession();
    }
  }
}

export type ErrorInfo = {
  field: string;
  error: string;
};
